import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import LocaleButtonGroup from 'csam/admin/components/LocaleButtonGroup';
import useTranslatableFields from 'csam/admin/hooks/UseTranslateble,';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed, getImage } from 'csam/utils/Constants';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface QuizData {
  year_id: number;
  status: number;
  title: string;
  link: string;
  image: string | File;
  [key: string]: string | number | File;
}

interface TranslationsData {
  title: string;
  link: string;
  image: string | File;
  [key: string]: unknown;
}

interface State {
  data: QuizData;
  translations: {
    es: TranslationsData;
    pt: TranslationsData;
    zh: TranslationsData;
  };
}

const AddDataProtectionTrainings = () => {
  const initialState = {
    data: { year_id: 0, title: '', link: '', image: '', status: 0 },
    translations: {
      es: { title: '', image: '', link: '' },
      pt: { title: '', image: '', link: '' },
      zh: { title: '', image: '', link: '' },
    },
  };

  const [trainingsData, setTrainingsData] = useState<State>(initialState);
  const [loading, setLoading] = useState(false);
  const imageName = trainingsData.data.image;

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { locale } = useContext(LocaleContext);

  const slug = `data_protection_tranings`;

  const { translatePending, errorTranslate, translateData, disabledFields, setLocale } = useTranslatableFields(
    slug,
    initialState,
  );

  useEffect(() => {
    if (!id) {
      setLocale('en');
    }
  }, [id, setLocale]);

  const query = useAuthenticatedQuery(
    ['trainings', id],
    {
      url: `${fixed}admin/en/data_protection_tranings/${id}`,
    },
    {
      enabled: !!id,
    },
  );

  useEffect(() => {
    if (query.data && query.data.success) {
      setTrainingsData(query.data.data as State);
    }
  }, [query.data]);

  const isUpdateForm = Boolean(id);

  const mutation = useAuthenticatedMutation(
    isUpdateForm ? ['update-trainings', 'PUT'] : ['add-trainings', 'POST'],

    (data) => {
      const headers = data instanceof FormData ? {} : { 'Content-Type': 'application/json' };

      return {
        url: `${fixed}admin/${locale}/data_protection_tranings${isUpdateForm ? `/${id}` : ''}`,

        method: isUpdateForm ? 'PUT' : 'POST',

        headers,

        data,
      };
    },
  );

  const {
    data: yearsData,
    error: yearsError,
    isPending: yearIsPending,
  } = useAuthenticatedQuery(['fetch-years', 'GET'], {
    url: `${fixed}admin/en/calender_years`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  interface YearData {
    id: number;
    year_id: string;
    year: string;
  }

  if (yearIsPending) return <AdminPageLoader />;
  if (yearsError) return <div>Error: {yearsError.message}</div>;
  if (yearsData && !yearsData.success) return <AdminInternalError />;

  if (translatePending) return <AdminPageLoader />;
  if (errorTranslate) return <div>Error: {errorTranslate.message}</div>;
  if (translateData && !translateData.success) return <AdminInternalError />;

  const handleChange = (event: { target: { name: string; value: string | number; checked?: boolean } }) => {
    // eslint-disable-next-line prefer-const
    let { name, value, checked } = event.target;

    if (name === 'status') {
      value = checked ? 1 : 0;
    }

    if (translateData) {
      const translatableFields = translateData.data;

      setTrainingsData((prevState) => {
        let newData = prevState.data;
        let newTranslations = prevState.translations;

        if (locale !== 'en' && translatableFields.includes(name)) {
          newTranslations = {
            ...prevState.translations,
            [locale as keyof typeof prevState.translations]: {
              ...(prevState.translations[locale as keyof typeof prevState.translations] || {}),
              [name]: value,
            },
          };
        } else {
          newData = {
            ...prevState.data,
            [name]: value,
          };
        }

        return {
          data: newData,
          translations: newTranslations,
        };
      });
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const date = new Date().toISOString();
      const tableName = 'data_protection_tranings';
      let fileName;

      if (locale === 'en') {
        fileName = `${date}_${tableName}_${file.name}`;
      } else {
        fileName = `${locale}_${tableName}_${file.name}`;
      }

      const newFile = new File([file], fileName, { type: file.type });

      setTrainingsData((prevData) => {
        let newData = { ...prevData.data };
        let newTranslations = { ...prevData.translations };
        if (locale === 'en') {
          newData = {
            ...newData,
            [event.target.name]: newFile,
          };
        } else {
          newTranslations = {
            ...newTranslations,
            [locale]: {
              ...(newTranslations[locale as keyof State['translations']] || {}),
              [event.target.name]: newFile,
            },
          };
        }
        return {
          data: newData,
          translations: newTranslations,
        };
      });
    }
  };

  const appendToFormData = (formData: FormData, data: Record<string, unknown>, baseKey: string) => {
    Object.entries(data).forEach(([key, value]) => {
      const fullKey = `${baseKey}[${key}]`;

      if (value instanceof File) {
        formData.append(fullKey, value);
      } else if (typeof value === 'object' && value !== null) {
        formData.append(fullKey, JSON.stringify(value));
      } else {
        formData.append(fullKey, String(value));
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);

    const formData = new FormData();

    const data = { ...trainingsData };

    if (data) {
      delete data.translations;
    }

    appendToFormData(formData, data, 'data');

    if (trainingsData.data.image instanceof File) {
      formData.append('image', trainingsData.data.image);
    }

    if (trainingsData?.translations) {
      Object.entries(trainingsData.translations).forEach(([localeKey, translation]) => {
        appendToFormData(formData, translation, `translations[${localeKey}]`);
      });
    }

    try {
      await mutation.mutateAsync(formData, {
        onSuccess: () => {
          toast.success(`Record ${isUpdateForm ? 'Updated' : 'Added'} Successfully`);
          setTimeout(() => {
            navigate('/admin/data-protection-training');
          }, 1000);
        },

        onError: () => {
          console.log('An error occurred while saving the data.');
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetLocale = (newLocale: string) => {
    if (locale === 'en' && newLocale !== 'en') {
      for (const key in trainingsData.data) {
        if (
          trainingsData.data[key] === undefined ||
          trainingsData.data[key] === '' ||
          (trainingsData.data[key] === 0 && key !== 'status')
        ) {
          toast.error(`Please fill in the ${key} field first`);
          return;
        }
      }
    }
    setLocale(newLocale);
  };

  return (
    <AdminWrapper>
      <Box className="pageHeader">
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="h6">
            {' '}
            {isUpdateForm ? 'Edit Data Protection Trainings' : 'Add Data Protection Trainings'}{' '}
          </Typography>
        </Box>
        <LocaleButtonGroup currentLocale={locale} setLocale={handleSetLocale} />
      </Box>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              fullWidth
              labelId="year-label"
              name="year_id"
              size="small"
              value={trainingsData.data.year_id === 0 ? '' : trainingsData.data.year_id.toString()}
              onChange={handleChange}
              disabled={disabledFields.includes('year_id')}
              required={locale === 'en'}
            >
              {(yearsData.data as YearData[]).map((year: YearData) => (
                <MenuItem key={year.id} value={year.id.toString()}>
                  {year.year}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item sm={6}>
            <InputLabel id="monthTitle-label">
              Title <Typography component="sup">{locale.toUpperCase()}</Typography>
            </InputLabel>
            <TextField
              placeholder="Add Title"
              size="small"
              name="title"
              value={
                locale === 'en'
                  ? trainingsData.data.title
                  : trainingsData.translations[locale as keyof State['translations']]?.title || ''
              }
              onChange={handleChange}
              disabled={disabledFields.includes('title')}
              required={locale === 'en'}
              fullWidth
            />
          </Grid>

          <Grid item sm={6}>
            <InputLabel id="monthTitle-label">
              Link <Typography component="sup">{locale.toUpperCase()}</Typography>
            </InputLabel>
            <TextField
              placeholder="Add Link"
              size="small"
              name="link"
              value={
                locale === 'en'
                  ? trainingsData.data.link
                  : trainingsData.translations[locale as keyof State['translations']]?.link || ''
              }
              onChange={handleChange}
              disabled={disabledFields.includes('link')}
              required={locale === 'en'}
              fullWidth
            />
          </Grid>

          <Grid item sm={12}>
            <InputLabel id="monthTitle-label">
              Image <Typography component="sup">{locale.toUpperCase()}</Typography>
            </InputLabel>
            <TextField
              type="file"
              name="image"
              onChange={handleImageChange}
              disabled={disabledFields.includes('image')}
              required={locale === 'en' && !imageName}
              fullWidth
            />
            {imageName && (
              <img
                className="formImage"
                src={`${getImage}data_protection_tranings/${locale === 'en' ? imageName : trainingsData.translations[locale as keyof typeof trainingsData.translations]?.image}`}
                alt=""
              />
            )}
          </Grid>

          <Grid item sm={6}>
            <InputLabel id="status-label">Status</InputLabel>
            <Grid container alignItems="center">
              <Typography>Inactive</Typography>
              <Switch
                id="status-label"
                name="status"
                onChange={handleChange}
                disabled={locale !== 'en'}
                checked={Boolean(trainingsData.data.status)}
              />
              <Typography>Active</Typography>
            </Grid>
          </Grid>

          <Grid item sm={12}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading && <CircularProgress size={16} sx={{ mr: 1 }} />} Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </AdminWrapper>
  );
};

export default AddDataProtectionTrainings;
